import { AbsgArrayHelper, LogService } from '@absg/angular-common';
import { StationDetails } from '@absg/api-station-management';
import { AccessPointDoorState, AccessPointLockState, BaseService, ModbusAccessPointNotification, ModbusAccessPointRequest, ModbusAccessPointResponse, NimAlertService } from '@absg/nim-client-shared';
import { Injectable, NgZone } from '@angular/core';
import { AccessPointRefreshStationResponse } from 'core.models/modbus/access-point-refresh-notification.model';
import { Observable, of, Subject } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { AccessPointEvents } from 'shared.root/constants/access-point-events';
import { ElectronApplicationService } from 'shared.services/electron-application.service';

import { ModbusService } from './modbus.service';

@Injectable({ providedIn: 'root' })
export class LockAccessService extends BaseService {
  private ipcRenderer: Electron.IpcRenderer;
  accessPointStateChangedNotification = new Subject<ModbusAccessPointNotification>();
  doorRefreshIds$ = new Subject<number>();
  virtualAccessPoints: number[] = [];
  openAccessPoints: number[] = [];
  failedAccessPoints: number[] = [];
  isSimulatorOn = false;
  isResolvingDoors = false;
  simulatedAccessPoints: number[] = [41, 42, 43, 44];
  unlockRequestOpenDoorDelay = 500;
  unlockRequestCloseDoorDelay = 2000;
  startListening = 'startListening';
  stopListening = 'stopListening';

  constructor(
    private electronApplicationService: ElectronApplicationService,
    private zone: NgZone,
    loggingService: LogService,
    private modbusService: ModbusService,
    private nimAlertService: NimAlertService,
  ) {
    super(loggingService);
    this.ipcRenderer = this.electronApplicationService.getIpcRenderer();
    this.startService();
  }

  startExpress(): void {
    this.ipcRenderer?.send(this.startListening);
  }

  stopExpress(): void {
    this.ipcRenderer?.send(this.stopListening);
  }

  private startService(): void {
    if (!this.ipcRenderer) {
      console.warn('not listening to lock access as ipcRenderer is null');
      return;
    }

    this.ipcRenderer.on(AccessPointEvents.accessPointChangeNotification,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (event: any, notification: ModbusAccessPointNotification) => {
        // NOTE: The use of zone.run() is needed to properly notify the UI of changes based on this event
        this.zone.run(() => {
          console.log(`${notification?.accessPointId} - fail ${notification?.failureFlag} - lock ${notification?.lockState} - door ${notification?.doorSensorState}`);
          this.handleMessage(`${notification?.accessPointId} - fail ${notification?.failureFlag} - lock ${notification?.lockState} - door ${notification?.doorSensorState}`);
          this.onAccessPointStateChangedNotification(event, notification);
        });
      });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.ipcRenderer.on(AccessPointEvents.doorRefreshNotification, (event: any, notification: AccessPointRefreshStationResponse) => {
      // NOTE: The use of zone.run() is needed to properly notify the UI of changes based on this event
      this.zone.run(() => {
        console.log(JSON.stringify(notification));
        this.handleMessage(JSON.stringify(notification));
        this.onDoorRefreshNotification(event, notification);
      });
    });
  }

  setSimulateMode(isSimulateMode: boolean): void {
    this.isSimulatorOn = isSimulateMode;
  }

  setVirtualDoors(station: StationDetails): void {
    const ids = [];
    AbsgArrayHelper.forEach(station?.accessPoints, ac => {
      if (ac.children && !ac.children[0]?.isPhysicalPosition) {
        ids.push(ac.id);
      }
    });
    this.virtualAccessPoints = ids;
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onAccessPointStateChangedNotification(__: any, notification: ModbusAccessPointNotification): void {
    if (this.isResolvingDoors) {
      this.checkFailedDoorResolve(notification);
    }
    if (notification) {
      this.accessPointStateChangedNotification.next(notification);
    } else {
      const errorMessage = 'Messaging Service received an IPC message for a door state change notification with no attached data.';
      this.handleErrorMessage(errorMessage);
    }
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onDoorRefreshNotification(__: any, response: AccessPointRefreshStationResponse): void {
    this.handleMessage(JSON.stringify(response));
    this.isResolvingDoors = false;
    this.doorRefreshIds$.next(response?.resetIds ? response?.resetIds[0] : 0);
    this.nimAlertService.doorRefreshResetAlert();
    if (!response.isAllFailuresCorrected) {
      this.triggerFailedDoor(1);
    }
  }

  cancelUnlockAccessPoint(accessPointId: number): Observable<boolean> {
    if (this.isSimulatorOn) { return of(true); }
    const request = this.generateRequest(accessPointId);
    return this.modbusService.cancelUnlockAccessPointPost(request).pipe(
      take(1),
      map(response => response.isSuccessful),
      catchError((error) => {
        this.handleError(error);
        return of(false);
      }),
    );
  }

  lockAccessPoint(accessPointId: number): Observable<boolean> {
    if (this.isSimulatorOn) { return of(true); }
    const request = this.generateRequest(accessPointId);
    return this.modbusService.lockAccessPointPost(request).pipe(
      take(1),
      map(response => response.isSuccessful),
      catchError((error) => {
        this.handleError(error);
        this.nimAlertService.showErrorAlert('Failed to lock', new Error('Communication might be conflicted.'));
        return of(false);
      }),
    );
  }

  unlockAccessPoint(accessPointId: number): Observable<ModbusAccessPointResponse> {
    if (this.isSimulatorOn) { return this.simulateDoorLogic(accessPointId); }
    const request = this.generateRequest(accessPointId);
    return this.modbusService.unlockAccessPointPost(request).pipe(
      take(1),
      map((result) => {
        if (!result.isSuccessful) {
          this.triggerFailedDoor(accessPointId);
        }
        return result;
      }),
      catchError((error) => {
        this.handleError(error);
        return of(null);
      }),
    );
  }

  resetAccessPoint(accessPointId: number): Observable<boolean> {
    if (this.isSimulatorOn) { return this.simulateDoorLogic(accessPointId).pipe(map(() => true)); }
    const request = this.generateRequest(accessPointId);
    return this.modbusService.resetAccessPointPost(request).pipe(
      take(1),
      map(response => response.isSuccessful),
      catchError((error) => {
        this.handleError(error);
        this.nimAlertService.showErrorAlert('Failed to reset', new Error('Try again or check communication with lock/sensor'));
        return of(false);
      }),
    );
  }

  checkAnyOpenDoors(): Observable<boolean> {
    if (this.isSimulatorOn) { return of(true); }
    return this.modbusService.checkAnyOpenDoorsGet().pipe(
      take(1),
      map(response => response?.length > 0),
      catchError((error) => {
        this.handleError(error);
        return of(false);
      }),
    );
  }

  executeCloseCalls(): Observable<boolean> {
    if (this.isSimulatorOn) { return of(true); }
    const request = this.generateRequest(0);
    return this.modbusService.executeCloseCallsPost(request).pipe(
      take(1),
      map(response => response.isSuccessful),
      catchError((error) => {
        this.handleError(error);
        return of(false);
      }),
    );
  }

  allOpenAccessPoints(): Observable<number[]> {
    if (this.isSimulatorOn) { return of([]); }
    return this.modbusService.checkAnyOpenDoorsGet().pipe(
      take(1),
      catchError((error) => {
        this.handleError(error);
        return of([]);
      }),
    );
  }

  failAllOpenAccessPoints(): Observable<boolean> {
    if (this.isSimulatorOn) { return of(true); }
    const request = this.generateRequest(0);
    return this.modbusService.failAllOpenAccessPointsPost(request).pipe(
      take(1),
      map((response) => response.isSuccessful),
      catchError((error) => {
        this.handleError(error);
        return of(false);
      }),
    );
  }

  getAllFailedDoors(): Observable<number[]> {
    if (this.isSimulatorOn) { return of(this.failedAccessPoints); }
    return this.modbusService.getAllFailedDoorsGet().pipe(
      take(1),
      catchError((error) => {
        if (error?.status === 0) {
          this.nimAlertService
            .showInfoAlert('Communication to station did not respond in a timely manner. Attempts to unlock doors may fail. If doors do not work, please call OSC for further assistance.');
        }
        this.handleError(error);
        return of([]);
      }),
    );
  }

  lockAllAccessPoints(): Observable<boolean> {
    if (this.isSimulatorOn) { return of(true); }
    const request = this.generateRequest(0);
    return this.modbusService.lockAllAccessPointsPost(request).pipe(
      take(1),
      map(response => response.isSuccessful),
      catchError((error) => {
        this.handleError(error);
        return of(false);
      }),
    );
  }

  resetAllAccessPoints(): Observable<boolean> {
    if (this.isSimulatorOn) { return of(true); }
    const request = this.generateRequest(0);
    return this.modbusService.resetAllAccessPointsPost(request).pipe(
      take(1),
      map(response => response.isSuccessful),
      catchError((error) => {
        this.handleError(error);
        return of(false);
      }),
    );
  }
  refreshStationConfiguration(): Observable<boolean> {
    if (this.isSimulatorOn) { return of(true); }
    return this.modbusService.refreshStationConfigurationPost(this.generateRequest(1)).pipe(
      take(1),
      map(response => response.isSuccessful),
      catchError((error) => {
        this.handleError(error);
        return of(false);
      }),
    );
  }

  private triggerFailedDoor(accessPointId: number): void {
    const newResponse = new ModbusAccessPointNotification(accessPointId);
    newResponse.failureFlag = true;
    newResponse.isResolvedDoor = false;
    this.accessPointStateChangedNotification.next(newResponse);
  }

  private generateRequest(accessPointId: number): ModbusAccessPointRequest {
    const request = new ModbusAccessPointRequest();
    request.accessPointId = accessPointId;
    request.requestId = this.createRequestId();
    return request;
  }
  private createRequestId(): string {
    // NOTE: We are using a basic timestamp for the following reasons
    // - Guids do not truly exist in JavaScript, and we are not operating in a distributed environment that
    //   would require a Guid (i.e., the station client and the Modbus service live on the same machine)
    // - UUIDs come in two main versions: version 1 and version 4
    //   https://blog.tompawlak.org/generate-unique-identifier-nodejs-javascript
    // - UUID v1 implements a time-based ID
    // - UUID v4 implements a randomized ID
    // - using a UUID would require another NPM package, which is undesirable
    // - Because of the Dispense workflow, requests are synchronous, so there is no risk of time-base collisions
    const now = new Date();
    return now.getTime().toString();
  }

  private checkFailedDoorResolve(notification: ModbusAccessPointNotification): void {
    notification.isResolvedDoor = true;
    if (notification.failureFlag === false && notification.doorSensorState === AccessPointDoorState.Closed) {
      const foundIndex = AbsgArrayHelper.findIndex(this.failedAccessPoints, ac => ac === notification.accessPointId);
      if (foundIndex > -1) {
        notification.isResolvedDoor = true;
        this.failedAccessPoints.splice(foundIndex, 1);
      }
    } else if (notification.failureFlag) {
      notification.isResolvedDoor = true;
      if (AbsgArrayHelper.findIndex(this.failedAccessPoints, ac => ac === notification.accessPointId) < 0) {
        this.failedAccessPoints.push(notification.accessPointId);
      }
    }
  }

  private simulateDoorLogic(accessPointId: number): Observable<ModbusAccessPointResponse> {
    const simulateResponse = new ModbusAccessPointNotification();
    simulateResponse.eventId = this.createRequestId();
    simulateResponse.accessPointId = accessPointId;
    if (AbsgArrayHelper.findIndex(this.virtualAccessPoints, ac => ac === accessPointId) > -1) {
      setTimeout(() => {
        simulateResponse.doorSensorState = AccessPointDoorState.Closed;
        simulateResponse.failureFlag = true;
        this.onAccessPointStateChangedNotification(null, simulateResponse);
      });
      const failResponse = new ModbusAccessPointResponse();
      failResponse.requestId = this.createRequestId();
      failResponse.isSuccessful = true;
      failResponse.accessPointId = accessPointId;
      failResponse.isSuccessful = false;
      this.handleError(new Error(`Tried to open a virtual lock for ${accessPointId} - this is not a valid call.`));
      return of(failResponse);
    }
    setTimeout(() => {
      // simulate an access point state change for door open.
      // note, the door is locked once opened so state will show locked. Failure false - let's not get too crazy with this just yet
      console.log(`Access Point Messaging: opening door - ${accessPointId}`);
      simulateResponse.doorSensorState = AccessPointDoorState.Open;
      this.onAccessPointStateChangedNotification(null, simulateResponse);
      setTimeout(() => {
        // simulate an access point state change for door close
        // door is locked and door is closed. Failure false - let's not get too crazy with this just yet
        console.log(`Access Point Messaging: closing door- ${accessPointId}`);
        simulateResponse.lockState = AccessPointLockState.Locked;
        simulateResponse.doorSensorState = AccessPointDoorState.Closed;
        simulateResponse.failureFlag = false;
        this.onAccessPointStateChangedNotification(null, simulateResponse);
      }, this.unlockRequestCloseDoorDelay);
      setTimeout(() => {
        // simulate an access point state change for modbus auto locking open door.
        console.log(`Access Point Messaging: auto-lock door - ${accessPointId}`);
        simulateResponse.doorSensorState = AccessPointDoorState.Open;
        simulateResponse.lockState = AccessPointLockState.Locked;
        this.onAccessPointStateChangedNotification(null, simulateResponse);
      }, this.unlockRequestOpenDoorDelay);
    }, this.unlockRequestOpenDoorDelay);
    const response = new ModbusAccessPointResponse();
    response.requestId = this.createRequestId();
    response.isSuccessful = true;
    response.accessPointId = accessPointId;
    return of(response);
  }
}
